<template>
  <div>
    <base-material-card
      icon="mdi-history"
      title="ประวัติการเบิกจ่ายงบประมาณพนักงานราชการ"
      class="px-5 py-3"
    >
      <!-- Filters -->
      <v-row class="mb-4" dense>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterYear"
            :items="availableYears"
            label="ปีงบประมาณ"
            outlined
            dense
            clearable
            @change="loadBudgetHistory"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterTime"
            :items="periodOptions"
            label="งวดรายงาน"
            outlined
            dense
            clearable
            @change="loadBudgetHistory"
          />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center">
          <v-btn
            color="primary"
            outlined
            @click="loadBudgetHistory"
            :loading="isLoading"
          >
            <v-icon class="mr-1">mdi-magnify</v-icon>
            ค้นหา
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <!-- No Data -->
      <v-alert
        v-if="!isLoading && groupedData.length === 0"
        type="info"
        outlined
      >
        ไม่พบข้อมูลประวัติการเบิกจ่าย
      </v-alert>

      <!-- Grouped by Report -->
      <div v-for="group in groupedData" :key="group.report_id" class="mb-6">
        <v-card outlined>
          <!-- Report Header -->
          <v-card-title class="pa-4 grey lighten-4">
            <v-row align="center" no-gutters>
              <v-col>
                <div class="text-subtitle-1 font-weight-bold">
                  <v-icon small class="mr-1">mdi-file-document</v-icon>
                  {{ group.reporting_period }}
                  — ปี {{ group.report_year }}
                </div>
                <div class="text-body-2 text-grey mt-1">
                  {{ group.college_name }}
                  | ผอ. {{ group.director_name }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-chip
                  :color="getStatusColor(group.status)"
                  text-color="white"
                  small
                >
                  {{ getStatusText(group.status) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-title>

          <v-divider />

          <!-- Summary Cards -->
          <v-card-text class="pa-4">
            <v-row dense class="mb-4">
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">งบจัดสรรทั้งงวด</div>
                <div class="text-h6 font-weight-bold text-primary">
                  ฿{{ formatCurrency(group.total_allocated) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">เบิกจ่ายทั้งงวด</div>
                <div class="text-h6 font-weight-bold text-green">
                  ฿{{ formatCurrency(group.total_disbursed) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">คงเหลือทั้งงวด</div>
                <div
                  class="text-h6 font-weight-bold"
                  :class="
                    group.total_remaining >= 0 ? 'text-orange' : 'text-red'
                  "
                >
                  ฿{{ formatCurrency(group.total_remaining) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">อัตราเบิกจ่าย</div>
                <div class="text-h6 font-weight-bold text-info">
                  {{
                    calcPercentage(
                      group.total_disbursed,
                      group.total_allocated
                    )
                  }}%
                </div>
              </v-col>
            </v-row>

            <v-progress-linear
              :value="
                calcPercentage(group.total_disbursed, group.total_allocated)
              "
              height="8"
              color="primary"
              class="mb-4"
            />

            <!-- Monthly Breakdown Table -->
            <v-simple-table dense>
              <thead>
                <tr class="grey lighten-3">
                  <th>เดือน</th>
                  <th class="text-right">จัดสรร (฿)</th>
                  <th class="text-right">เบิกจ่าย (฿)</th>
                  <th class="text-right">คงเหลือ (฿)</th>
                  <th class="text-right">อัตรา (%)</th>
                  <th>หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="month in group.months" :key="month.id">
                  <td>
                    <v-icon x-small class="mr-1">mdi-calendar-month</v-icon>
                    {{ getMonthName(month.month) }}
                  </td>
                  <td class="text-right">
                    {{ formatCurrency(month.allocated_budget) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCurrency(month.disbursed_budget) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="month.remaining_budget < 0 ? 'text-red' : ''"
                  >
                    {{ formatCurrency(month.remaining_budget) }}
                  </td>
                  <td class="text-right">
                    <v-chip
                      x-small
                      :color="
                        getPercentageColor(
                          calcPercentage(
                            month.disbursed_budget,
                            month.allocated_budget
                          )
                        )
                      "
                      text-color="white"
                    >
                      {{
                        calcPercentage(
                          month.disbursed_budget,
                          month.allocated_budget
                        )
                      }}%
                    </v-chip>
                  </td>
                  <td class="text-body-2 text-grey">
                    {{ month.notes || "-" }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </div>
    </base-material-card>
  </div>
</template>

<script>
export default {
  props: {
    user: { type: [Array, Object] },
    period_college: { type: [Array, Object] }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      isLoading: false,
      filterYear: null,
      filterTime: null,
      budgetData: [],
      availableYears: [],
      periodOptions: [
        { text: "งวดที่ 1", value: 1 },
        { text: "งวดที่ 2", value: 2 },
        { text: "งวดที่ 3", value: 3 },
        { text: "งวดที่ 4", value: 4 }
      ],
      monthNames: [
        "",
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม"
      ]
    };
  },

  computed: {
    /** Group flat rows by report_id */
    groupedData() {
      const map = {};
      for (const row of this.budgetData) {
        const rid = row.report_id;
        if (!map[rid]) {
          map[rid] = {
            report_id: rid,
            college_name: row.college_name,
            report_year: row.report_year,
            report_time: row.report_time,
            status: row.status,
            reporting_period: row.reporting_period,
            director_name: row.director_name,
            total_allocated: parseFloat(row.total_allocated) || 0,
            total_disbursed: parseFloat(row.total_disbursed) || 0,
            total_remaining: parseFloat(row.total_remaining) || 0,
            months: []
          };
        }
        map[rid].months.push(row);
      }
      return Object.values(map);
    }
  },

  async mounted() {
    this.initYears();
    this.filterYear = this.period_college.period_college_year || null;
    this.filterTime = this.period_college.period_college_times || null;
    await this.loadBudgetHistory();
  },

  methods: {
    initYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.availableYears = [];
      for (let i = 0; i <= 5; i++) {
        this.availableYears.push(currentYear - i);
      }
    },

    async loadBudgetHistory() {
      this.isLoading = true;
      try {
        const payload = {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code || ""
        };
        if (this.filterYear) payload.report_year = this.filterYear;
        if (this.filterTime) payload.report_time = this.filterTime;
        const response = await this.$http.post(
          "government_employee_report_monthly_budget.read.php",
          payload
        );
        this.budgetData = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading budget history:", error);
        this.budgetData = [];
      } finally {
        this.isLoading = false;
      }
    },

    getMonthName(val) {
      return this.monthNames[parseInt(val)] || `เดือน ${val}`;
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    calcPercentage(disbursed, allocated) {
      const a = parseFloat(allocated) || 0;
      if (a === 0) return 0;
      return Math.round((parseFloat(disbursed || 0) / a) * 100);
    },

    getPercentageColor(pct) {
      if (pct >= 80) return "green";
      if (pct >= 50) return "orange";
      return "red";
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        submitted: "blue",
        approved: "green",
        rejected: "red",
        cancelled: "orange",
        checking: "purple"
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        draft: "ร่าง",
        submitted: "รอการอนุมัติ",
        approved: "อนุมัติแล้ว",
        rejected: "ปฏิเสธ",
        cancelled: "ยกเลิก",
        checking: "กำลังตรวจสอบ"
      };
      return texts[status] || status;
    }
  }
};
</script>

<style scoped>
.text-green {
  color: #4caf50;
}
.text-orange {
  color: #ff9800;
}
.text-red {
  color: #f44336;
}
.text-info {
  color: #2196f3;
}
.text-primary {
  color: #1976d2;
}
.text-grey {
  color: #757575;
}
</style>
